<template>
  <div class="group group--contentful group--sidebar" ref="container">
    <span class="visuallyhidden">{{ scrollDistance }}</span>

    <h3 
      :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
      class="h3 fw-bold mb" 
      v-if="showHighlights">Featured content:</h3>

    <ul v-if="showHighlights" class="list list--articles mt">
      <li
        v-for="(article, index) in highlightedArticles"
        :key="article.id"
        class="list__item m0"
      >
        <a
          class="link p tdn m0 df df--column"
          @click="navigateToArticle(article)"
        >
          <h4 
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h4 c-accent fw-bold m0">{{ article.name }}</h4>
          <span>{{ article.sub }}</span>
        </a>

        <hr v-if="index < highlightedArticles.length - 1" class="hr">
      </li>
    </ul>

    <hr v-if="showHighlights" class="hr mb2">
    
    <div
      v-for="(category, catIndex) in categoriesToShow"
      :key="category.id"
      class="list__container"
    >
      <div
        :class="[
          `list__header`,
          {
            'list__header--image': category.image,
          },
        ]"
        :style="getColour(catIndex)"
      >
        <div class="list__inner">
          <h3
            v-if="category.topics.length"
            :id="category.slug"
            :ref="`scroll-${category.slug}`"
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="h3 fw-medium mt0 fw-bold"
          >
            {{ category.name }}
            
          </h3>
          
          <p v-if="category.categoryDescription">
            {{ category.categoryDescription }}
          </p>
        </div>
        <!-- <img
          v-if="category.image"
          :class="[`list__image`]"
          :src="category.image"
          :alt="category.name"
        > -->
      </div>
      
      <ul v-if="category.topics.length" class="list list--topics">
        <li
          v-for="(topic, index) in category.topics"
          :key="topic.id"
          :style="getColour2(catIndex)"
          :class="[
            `list__item list__item--topic`,
            category.limit && category.limit <= index
              ? `list__item--hidden`
              : ``,
          ]"
          @click="topicDetails(topic)"
        >
          <div class="list__item--content">
            <div v-if="topic.name =='7BQRx9ZvT4Vy4ZR9l0A5iW'">
              <img
                v-if="category.image"
                :class="[`list__image`]"
                src="client\assets\images\home\home-illustration.png"
                :alt="category.name"
              >
            </div>
            <h4 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="h4">{{ topic.name }}</h4>
            
            <p v-if="topic.description" class="p">{{ topic.description }}</p>
          </div>
        </li>

        <li
          v-if="category.topics.length > 3"
          :class="[
            `list__item list__item--expand`,
            expandedCategories[category.id]
              ? `list__item--${category.topics.length % 3}`
              : `list__item--0`,
          ]"
        >
          <span
            :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
            class="c-accent fw-bold"
            @click="expandOrCollapseCategory(category.id)"
          >
            See {{ expandedCategories[category.id] ? `less` : `more` }}
          </span>
        </li>
      </ul>
    </div>

    <module-modal
      :modal="modalExternalData"
      v-show="showDisclaimer"
      @close="showDisclaimer = false"
    >
      <template slot="modal-footer">
        <module-button @click.native="showDisclaimer = false">No</module-button>
        <module-button @click.native="goToUrl">Yes</module-button>
      </template>
    </module-modal>

    <div class="contentful__footer" />
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

import ModuleModal from 'components/Modal';

// Utils
import { toSlug } from 'utils/string';

export default {
  name: 'view-topics',

  components: {
    ModuleModal,
  },

  // Data
  data() {
    return {
      colours: [
        '#D9F5F5',
        '#EBE4FF',
        '#F5F5F5',
        '#E1EDFF',
        '#EBE4FF',
        '#D9F5F5',
        '#F5F5F5',
        '#E1EDFF',
        '#EBE4FF',
        '#D9F5F5',
      ],
      expandedCategories: {},
      sections: {},
      scrollDistance: 0,
      modalExternalData: {
        title: 'You are now leaving the website.',
        copy: 'Your destination website is not controlled by our privacy statement. Please read the privacy statement of your destination website. Do you want to continue?',
      },
      showDisclaimer: false,
      preUrl: null,
    };
  },

  // Computed
  computed: {
    ...mapGetters('contentful', ['categories', 'highlightedArticles']),
    ...mapGetters('app', ['org']),
    ...mapGetters('sidebar', ['defaultOffset']),

    categoriesToShow() {
      return _.map(this.categories, (category) => {
        // eslint-disable-line no-undef
        category.topics =
          _.filter(category.topics, (topic) =>
            !topic.noAccess
              ? topic
              : topic.noAccess.indexOf(this.org.parent_code) === -1
          ) &&
          _.filter(category.topics, (topic) =>
            !topic.exclusiveAccess
              ? topic
              : topic.exclusiveAccess.indexOf(this.org.parent_code) !== -1
          ); // eslint-disable-line no-undef
        category.slug = toSlug(category.name);

        const obj = {
          limit: this.expandedCategories[category.id] ? 0 : 3,
          ...category,
        };

        return obj;
      });
    },

    showHighlights() {
      return this.highlightedArticles && this.highlightedArticles.length;
    },
  },

  // Watchers
  watch: {
    org(org) {
      if (!org) return;
      this.requestHighlightedArticles();
    },
  },

  // Methods
  methods: {
    ...mapActions('contentful', ['getCategories', 'getHighlightedArticles']),
    ...mapActions('sidebar', ['saveActiveSection', 'saveDefaultOffset']),

    requestHighlightedArticles() {
      if (this.org.parent_code) {
        this.getHighlightedArticles({
          parentCode: this.org.parent_code,
        });
      }
    },

    topicDetails(topic) {
      const { id, name } = topic;

      this.$router.push({
        name: 'topic',
        params: {
          topic: toSlug(name),
        },
        query: {
          topicId: id,
        },
      });
    },

    goToUrl() {
      window.open(this.preUrl, '_blank');
      this.showDisclaimer = false;
    },

    navigateToArticle(article) {
      const { externalUrl } = article;

      if (externalUrl && this.org.show_disclaimer) {
        this.showDisclaimer = true;
        this.preUrl = externalUrl;
      } else if (externalUrl && !this.org.show_disclaimer) {
        this.preUrl = externalUrl;
        this.goToUrl();
      } else {
        this.$router.push({
          name: 'article',
          params: {
            article: toSlug(article.name),
          },
          query: {
            ...this.$route.query,
            articleId: article.id,
          },
        });
      }
    },

    getColour(index) {
      const hexToRgb = hex =>
        hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
          ,(m, r, g, b) => `#${  r  }${r  }${g  }${g  }${b  }${b}`)
          .substring(1).match(/.{2}/g)
          .map(x => parseInt(x, 16));
      const 
        bgColor = ((this.org.custom_color2 !== null)? this.org.custom_color2 : this.colours[index % 10]);
      const bgColor2 = hexToRgb(bgColor);
      bgColor2.push(0.6);
      return { 'background-color': (`${'rgb' + '('}${ bgColor2.join(', ')})`) };
    },

    getColour2(index) {
      const hexToRgb = hex =>
        hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
          ,(m, r, g, b) => `#${  r  }${r  }${g  }${g  }${b  }${b}`)
          .substring(1).match(/.{2}/g)
          .map(x => parseInt(x, 16));
      const 
        bgColor = ((this.org.custom_color2 !== null)? this.org.custom_color2 : this.colours[index % 10]);
      const bgColor2 = hexToRgb(bgColor);
      bgColor2.push(0.3);
      return { 'background-color': (`${'rgb' + '('}${ bgColor2.join(', ')})`) };
    },

    expandOrCollapseCategory(id) {
      this.$set(this.expandedCategories, id, !this.expandedCategories[id]);
    },

    setActiveSection() {
      _.forEach(this.$refs, (ref, key) => {
        // eslint-disable-line no-undef
        if (
          key.split('-')[0] === 'scroll' &&
          ref[0] &&
          ref[0].getBoundingClientRect().top <= this.defaultOffset
        )
          this.saveActiveSection(ref[0].id);
      });
    },

    handleScroll() {
      const container =
        this.$refs.container.getBoundingClientRect().top - this.defaultOffset;
      this.$set(this, 'scrollDistance', container);
    },
  },

  // Lifecycles
  created() {
    this.getCategories();
    this.requestHighlightedArticles();
    window.addEventListener('scroll', this.handleScroll);
  },

  mounted() {
    this.saveDefaultOffset(this.$refs.container.getBoundingClientRect().top);
  },

  updated() {
    this.setActiveSection();
  },

  destroyed() {
    window.removeEventListener('scroll', this.handleScroll);
  },
};
</script>



// WEBPACK FOOTER //
// Topics.vue?a664e42c